import React, { useState } from 'react';
import { useLang } from '../lib/i18n';
import { ResponsiveContainer, ComposedChart, Bar, Line, Area, BarChart, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, LabelList } from 'recharts';
import { Printer, ChevronLeft, ChevronRight, Euro, Receipt, CalendarDays, TrendingUp } from 'lucide-react';
import PageShell from '../components/PageShell';
import { SectionCard, KpiTile, ChartTooltip, PillToggle, PageHeader, QueryState, CALUM, Loader, ErrorBox } from '../components/charts';
import ReceiptsModal from '../components/ReceiptsModal';
import { useApi } from '../hooks/useApi';
import api from '../lib/api';
import { todayISO, isToday, isYesterday } from '../lib/day';
import { formatCurrency, formatCurrency2, formatNumber, formatDate, formatLang, formatMonth, weekdayByIndex, weekdayLabel, weekdayPlural, weekdayShort } from '../lib/format';

/*
 * Overzicht — the one performance page. Pick today, any week / month / quarter /
 * year (or a custom range), compare it with the average same weekday, last year,
 * the previous period or any other range; one backend call (period-report)
 * powers the KPIs, the daily chart, omzetgroepen, dagdelen & weekdagen,
 * stijgers/dalers and dag-per-dag.
 * Prognose and Groei have a fixed horizon (current month / year) on purpose.
 *
 * It opens on today, because the first question is almost always "how are we
 * doing right now?" — and today only means something next to a normal day, so
 * the default comparison is the average of the last 8 same weekdays. Every KPI
 * card opens the receipts behind it.
 */

// --- UTC-safe date helpers (toISOString on local midnight shifts a day in UTC+2)
const parseISO = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const iso = (dt) => dt.toISOString().slice(0, 10);
const addDays = (dt, n) => new Date(dt.getTime() + n * 86400000);
const MONTHS_FULL = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

function isoWeek(dt) {
  const d = new Date(dt.getTime());
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return { year: d.getUTCFullYear(), week: Math.ceil(((d - yearStart) / 86400000 + 1) / 7) };
}

function windowFor(type, anchorISO) {
  const a = parseISO(anchorISO);
  const y = a.getUTCFullYear(), m = a.getUTCMonth();
  if (type === 'day') return { start: anchorISO, end: anchorISO };
  if (type === 'week') {
    const mon = addDays(a, -((a.getUTCDay() + 6) % 7));
    return { start: iso(mon), end: iso(addDays(mon, 6)) };
  }
  if (type === 'month') return { start: iso(new Date(Date.UTC(y, m, 1))), end: iso(new Date(Date.UTC(y, m + 1, 0))) };
  if (type === 'quarter') {
    const q = Math.floor(m / 3) * 3;
    return { start: iso(new Date(Date.UTC(y, q, 1))), end: iso(new Date(Date.UTC(y, q + 3, 0))) };
  }
  if (type === 'year') return { start: `${y}-01-01`, end: `${y}-12-31` };
  return null;
}

// same calendar date one year earlier (Feb 29 -> 28)
function yearBack(s) {
  const [y, m, d] = s.split('-').map(Number);
  const day = m === 2 && d === 29 ? 28 : d;
  return `${y - 1}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// aggregate a daily series into ISO weeks (for quarter/year charts)
function toWeekly(rows) {
  const by = new Map();
  (rows || []).forEach((r) => {
    const wk = isoWeek(parseISO(r.date));
    const k = `${wk.year}-${String(wk.week).padStart(2, '0')}`;
    const cur = by.get(k) || { label: `${formatLang() === 'fr' ? 'S' : 'W'}${wk.week}`, revenue: 0, receipts: 0 };
    cur.revenue += r.revenue;
    cur.receipts += r.receipts;
    by.set(k, cur);
  });
  return [...by.keys()].sort().map((k) => by.get(k));
}

/**
 * Y-axis ticks for the hourly chart.
 *
 * A correction rung up before opening can start the day a few euro below zero,
 * and Recharts rounds its own ticks outwards — a €58 refund then reserves a
 * €750 band under the axis and squashes the actual day into the top half. So
 * we hand it the ticks: round steps from zero up, plus one tick at the real
 * minimum, which keeps the negative sliver proportional to what it is.
 */
function moneyTicks(rows, keys) {
  const vals = (rows || []).flatMap((r) => keys.map((k) => r[k])).filter((v) => v != null);
  if (!vals.length) return null;
  const max = Math.max(0, ...vals);
  const min = Math.min(0, ...vals);
  const rough = max / 4 || 1;
  const mag = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((v) => v >= rough) || mag * 10;
  const ticks = [];
  for (let v = 0; v <= max + step / 2; v += step) ticks.push(Math.round(v));
  if (min < 0) ticks.unshift(Math.round(min));
  return { ticks, domain: [ticks[0], ticks[ticks.length - 1]] };
}

// "+12%" above an hour's bar / point — the gap with a normal day, in place.
const DeltaLabel = ({ x, y, width, value }) => {
  if (value == null || x == null) return null;
  const up = value >= 0;
  return (
    <text x={x + (width || 0) / 2} y={y - 5} textAnchor="middle" fontSize={10} fontWeight={600}
      fill={up ? '#059669' : '#ef4444'}>
      {up ? '+' : ''}{String(value).replace('.', ',')}%
    </text>
  );
};

const Pct = ({ v }) => v == null ? <span className="text-slate-300">—</span> : (
  <span className={v >= 0 ? 'text-emerald-600' : 'text-red-500'}>{v >= 0 ? '+' : ''}{String(v).replace('.', ',')}%</span>
);

// Revenue groups (LightSpeed "Omzetgroepen") — colour + Dutch label per top category.
const GRP_COLORS = {
  Drinks: CALUM.gold, Food: CALUM.green, Extras: CALUM.mint, Glaces: CALUM.clay,
  'Bouillons & Autre': '#a78bfa', Producten: CALUM.slate,
};
const GRP_LABEL = {
  Drinks: 'Dranken', Food: 'Keuken', Extras: 'Diversen', Glaces: 'IJs',
  'Bouillons & Autre': 'Bouillons', Producten: 'Producten',
};
const grpColor = (g) => GRP_COLORS[g] || '#94a3b8';
// Language-aware short month name from a 1-based month number ("jan" / "janv").
const monthShort = (m) => formatMonth(`2000-${String(m).padStart(2, '0')}`).replace(/\s'\d\d$/, '');

export default function Overview() {
  const { t, lang } = useLang();
  const dr = useApi(() => api.dataRange(), []);
  const dmax = dr.data?.end;

  const [type, setType] = useState('day');
  const [anchor, setAnchor] = useState(null);          // ISO date inside the period
  const [custom, setCustom] = useState({ start: '', end: '' });
  const [cmpMode, setCmpMode] = useState('avg');       // avg | avgYtd | ly | prev | custom
  const [cmpCustom, setCmpCustom] = useState({ start: '', end: '' });
  const [drill, setDrill] = useState(null);            // receipts modal { start, end, label }
  const [hourMode, setHourMode] = useState('cum');     // cum | hour — hourly chart

  // A day view anchors on the real calendar day, not on the last day with data:
  // "today" must stay today even before the first receipt of the morning.
  const anchorISO = anchor || (type === 'day' ? todayISO() : dmax);
  const win = type === 'custom'
    ? (custom.start && custom.end && custom.start <= custom.end ? custom : null)
    : (anchorISO ? windowFor(type, anchorISO) : null);

  // The average-same-weekday benchmarks are built server-side (they are a set of
  // days, not a range), so there is no client-side comparison window for them:
  // 'avg' = the last 8 same weekdays, 'avgYtd' = every one since 1 January.
  const avgMode = cmpMode === 'avg' || cmpMode === 'avgYtd';
  let cmp = null;
  if (win && !avgMode) {
    if (cmpMode === 'ly') {
      cmp = type === 'week'
        ? { start: iso(addDays(parseISO(win.start), -364)), end: iso(addDays(parseISO(win.end), -364)) }
        : { start: yearBack(win.start), end: yearBack(win.end) };
    } else if (cmpMode === 'prev') {
      const s = parseISO(win.start);
      const y = s.getUTCFullYear(), m = s.getUTCMonth();
      if (type === 'month') cmp = { start: iso(new Date(Date.UTC(y, m - 1, 1))), end: iso(new Date(Date.UTC(y, m, 0))) };
      else if (type === 'quarter') cmp = { start: iso(new Date(Date.UTC(y, m - 3, 1))), end: iso(new Date(Date.UTC(y, m, 0))) };
      else if (type === 'year') cmp = { start: `${y - 1}-01-01`, end: `${y - 1}-12-31` };
      else {
        const len = (parseISO(win.end) - s) / 86400000 + 1;
        cmp = { start: iso(addDays(s, -len)), end: iso(addDays(s, -1)) };
      }
    } else if (cmpCustom.start && cmpCustom.end && cmpCustom.start <= cmpCustom.end) {
      cmp = cmpCustom;
    }
  }

  const rep = useApi(
    () => (win && (avgMode || cmp)
      ? api.periodReport({
        start_date: win.start,
        end_date: win.end,
        ...(avgMode
          ? { cmp_mode: cmpMode === 'avgYtd' ? 'avg_dow_ytd' : 'avg_dow' }
          : { cmp_start: cmp.start, cmp_end: cmp.end }),
      })
      : Promise.resolve(null)),
    [win?.start, win?.end, cmp?.start, cmp?.end, cmpMode],
  );
  const r = rep.data;
  const gr = useApi(() => api.growth(), []);
  const fc = useApi(() => api.forecast(), []);
  const gd = gr.data;
  const yoyMonths = (gd?.months || []).map((m) => ({ ...m, label: monthShort(m.month) }));
  const fixedNote = t("Altijd het volledige jaar, los van de gekozen periode");

  const periodLabel = (() => {
    if (!win) return '…';
    const s = parseISO(win.start);
    if (type === 'day') {
      if (isToday(win.start)) return t('Vandaag');
      if (isYesterday(win.start)) return t('Gisteren');
      return `${weekdayByIndex((s.getUTCDay() + 6) % 7)} ${formatDate(win.start)}`;
    }
    if (type === 'week') { const w = isoWeek(s); return `${t('Week')} ${w.week} · ${w.year}`; }
    if (type === 'month') return `${t(MONTHS_FULL[s.getUTCMonth()])} ${s.getUTCFullYear()}`;
    if (type === 'quarter') return `${lang === 'fr' ? 'T' : 'Q'}${Math.floor(s.getUTCMonth() / 3) + 1} ${s.getUTCFullYear()}`;
    if (type === 'year') return `${s.getUTCFullYear()}`;
    return `${formatDate(win.start)} – ${formatDate(win.end)}`;
  })();
  const cmpLabel = cmpMode === 'avgYtd' ? t('vs een normale dag dit jaar')
    : avgMode ? t('vs een normale dag')
    : cmpMode === 'ly' ? t('vs vorig jaar')
    : cmpMode === 'prev' ? t('vs vorige periode') : t('vs eigen keuze');
  const cmpWord = avgMode ? t('normaal') : t('toen');

  // Spell out what the benchmark is made of: "gemiddelde van de laatste 8
  // donderdagen", or year-to-date "gemiddelde van de 34 donderdagen dit jaar".
  const avgWeekdays = Object.entries(r?.cmp_avg?.per_weekday || {});
  const ytdScope = r?.cmp_avg?.scope === 'ytd';
  const avgDesc = !r?.cmp_avg ? ''
    : avgWeekdays.length === 1
      ? (ytdScope
        ? `${t('gemiddelde van de')} ${avgWeekdays[0][1].days} ${weekdayPlural(avgWeekdays[0][0])} ${t('dit jaar')}`
        : `${t('gemiddelde van de laatste')} ${avgWeekdays[0][1].days} ${weekdayPlural(avgWeekdays[0][0])}`)
      : (ytdScope
        ? `${t('gemiddelde per weekdag over')} ${r.cmp_avg.days} ${t('open dagen dit jaar')}`
        : `${t('gemiddelde per weekdag van de laatste')} ${r.cmp_avg.weeks} ${t('weken')}`);
  // Half days and one-off monsters would drag that average; they are left out.
  const excluded = r?.cmp_avg?.excluded || [];
  const excludedNote = excluded.length
    ? `${excluded.length} ${t(excluded.length === 1 ? 'uitzonderlijke dag niet meegeteld' : 'uitzonderlijke dagen niet meegeteld')}`
    : '';
  const openDrill = (start, end, label) => setDrill({ start, end, label });
  const hourAxis = moneyTicks(r?.hourly?.hours,
    hourMode === 'cum' ? ['cum_revenue', 'cmp_cum_revenue'] : ['revenue', 'cmp_revenue']);

  const shift = (dir) => {
    if (!win || type === 'custom') return;
    setAnchor(dir < 0 ? iso(addDays(parseISO(win.start), -1)) : iso(addDays(parseISO(win.end), 1)));
  };

  // daily chart — aggregate long ranges to weeks so the bars stay readable
  const weeklyMode = (r?.daily?.length || 0) > 40;
  const curRows = weeklyMode ? toWeekly(r?.daily) : (r?.daily || []);
  const cmpRows = weeklyMode ? toWeekly(r?.cmp_daily) : (r?.cmp_daily || []);
  const chart = Array.from({ length: Math.max(curRows.length, cmpRows.length) }, (_, i) => ({
    label: weeklyMode
      ? (curRows[i]?.label ?? cmpRows[i]?.label ?? i + 1)
      : (curRows[i] ? `${weekdayShort(curRows[i].weekday_nl)} ${curRows[i].date.slice(8)}` : cmpRows[i]?.date.slice(8) ?? i + 1),
    revenue: curRows[i] ? Math.round(curRows[i].revenue) : null,
    cmp_revenue: cmpRows[i] ? Math.round(cmpRows[i].revenue) : null,
  }));

  const dateInput = (value, onChange) => (
    <input type="date" value={value} min={dr.data?.start} max={dr.data?.end} onChange={onChange}
      className="bg-white rounded border border-slate-200 px-1.5 py-1.5 sm:py-0.5 text-[16px] sm:text-[11px] text-[#114817] focus:outline-none focus:border-[#cf9a40]" />
  );

  return (
    <PageShell page="Overzicht" accent="gold" freshness={dmax ? `${t('Data t/m')} ${formatDate(dmax)}` : ''}>
      <PageHeader
        title={<>{t('Overzicht')} — {periodLabel}</>}
        subtitle={<>
          {win && (win.start === win.end ? formatDate(win.start) : `${formatDate(win.start)} – ${formatDate(win.end)}`)}
          {cmp && <> · {t('vergeleken met')} {formatDate(cmp.start)} – {formatDate(cmp.end)}</>}
          {avgMode && avgDesc && <> · {t('vergeleken met het')} {avgDesc}</>}
          {excludedNote && <> · <span title={excluded.map((d) => `${formatDate(d.date)}: ${formatCurrency(d.revenue)}`).join(' · ')}>{excludedNote}</span></>}
        </>}>
        <div className="flex items-center gap-2 flex-wrap print:hidden">
          <PillToggle value={type} onChange={(v) => {
            setType(v);
            setAnchor(null);
            // A single day is only meaningful next to a normal day of the same
            // weekday; longer periods read better against last year.
            setCmpMode(v === 'day' ? 'avg' : 'ly');
          }} options={[
            { value: 'day', label: t('Dag') }, { value: 'week', label: t('Week') },
            { value: 'month', label: t('Maand') }, { value: 'quarter', label: t('Kwartaal') },
            { value: 'year', label: t('Jaar') }, { value: 'custom', label: t('Aangepast') },
          ]} />
          {type !== 'custom' ? (
            <div className="inline-flex items-center bg-slate-100 rounded-md p-0.5">
              <button onClick={() => shift(-1)} title={t('Vorige')} aria-label={t('Vorige')}
                className="p-2.5 sm:p-1.5 rounded hover:bg-white text-slate-500"><ChevronLeft className="h-4 w-4" /></button>
              <button onClick={() => shift(1)} title={t('Volgende')} aria-label={t('Volgende')}
                className="p-2.5 sm:p-1.5 rounded hover:bg-white text-slate-500"><ChevronRight className="h-4 w-4" /></button>
            </div>
          ) : (
            <div className="inline-flex items-center gap-1 bg-slate-100 rounded-md px-2 py-1">
              {dateInput(custom.start, (e) => setCustom({ ...custom, start: e.target.value }))}
              <span className="text-slate-400 text-[11px]">{t('tot')}</span>
              {dateInput(custom.end, (e) => setCustom({ ...custom, end: e.target.value }))}
            </div>
          )}
          <PillToggle value={cmpMode} onChange={setCmpMode} options={[
            { value: 'avg', label: type === 'day' ? t('Gemiddelde dag') : t('Gemiddelde weekdag') },
            { value: 'avgYtd', label: t('Gemiddeld dit jaar') },
            { value: 'ly', label: t('Vorig jaar') }, { value: 'prev', label: t('Vorige periode') },
            { value: 'custom', label: t('Andere periode') },
          ]} />
          {cmpMode === 'custom' && (
            <div className="inline-flex items-center gap-1 bg-slate-100 rounded-md px-2 py-1">
              {dateInput(cmpCustom.start, (e) => setCmpCustom({ ...cmpCustom, start: e.target.value }))}
              <span className="text-slate-400 text-[11px]">{t('tot')}</span>
              {dateInput(cmpCustom.end, (e) => setCmpCustom({ ...cmpCustom, end: e.target.value }))}
            </div>
          )}
          <button onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-md bg-[#114817] text-white text-xs sm:text-[11px] font-medium px-3 py-2 sm:py-1.5 hover:bg-[#186020]">
            <Printer className="h-3.5 w-3.5" /> {t('Afdrukken')}
          </button>
        </div>
      </PageHeader>

      {dr.error && <ErrorBox error={dr.error} onRetry={dr.reload} />}
      {rep.error && <ErrorBox error={rep.error} onRetry={rep.reload} />}
      {(rep.loading || dr.loading) && <Loader />}
      {!rep.loading && !dr.loading && !r && !rep.error && !dr.error && (
        <p className="text-sm text-slate-400 mb-4">{t('Kies een periode (en eventueel een vergelijkingsperiode).')}</p>
      )}

      {r && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {(() => {
            const drillProps = { onClick: () => openDrill(win.start, win.end, periodLabel), actionLabel: t('bekijk de bonnen') };
            // A day still in progress is judged against the same time of day —
            // measuring half a day against a whole one is always "behind".
            // The full-day figure stays visible underneath as the second line.
            const td = r.hourly?.to_date;
            const live = (pct, whole, normalNow) => td
              ? { delta: pct, deltaLabel: `${t('vs')} ${r.hourly.as_of} ${t('op een normale dag')}`,
                  delta2: whole, delta2Label: t('vs de hele dag'), sub: `${cmpWord} ${t('om')} ${r.hourly.as_of}: ${normalNow}` }
              : { delta: whole, deltaLabel: cmpLabel };
            return <>
              <KpiTile {...drillProps} label={t('Omzet')} value={formatCurrency(td ? td.revenue : r.kpis.revenue)}
                {...live(td?.revenue_pct, r.deltas.revenue, formatCurrency(td?.cmp_revenue ?? 0))}
                sub={td ? `${cmpWord} ${t('om')} ${r.hourly.as_of}: ${formatCurrency(td.cmp_revenue)} · ${t('hele dag')}: ${formatCurrency(r.cmp_kpis.revenue)}`
                  : `${cmpWord}: ${formatCurrency(r.cmp_kpis.revenue)}`} accent="gold" icon={Euro} />
              <KpiTile {...drillProps} label={t('Bonnen')} value={formatNumber(td ? td.receipts : r.kpis.receipts)}
                {...live(td?.receipts_pct, r.deltas.receipts, formatNumber(td?.cmp_receipts ?? 0))}
                sub={td ? `${cmpWord} ${t('om')} ${r.hourly.as_of}: ${formatNumber(td.cmp_receipts)} · ${t('hele dag')}: ${formatNumber(r.cmp_kpis.receipts)}`
                  : `${cmpWord}: ${formatNumber(r.cmp_kpis.receipts)}`} accent="green" icon={Receipt} />
              <KpiTile {...drillProps} label={t('Gemiddeld per bon')} value={formatCurrency2(td ? td.avg_per_receipt : r.kpis.avg_per_receipt)}
                {...live(td?.avg_per_receipt_pct, r.deltas.avg_per_receipt, formatCurrency2(td?.cmp_avg_per_receipt ?? 0))}
                sub={td ? `${cmpWord} ${t('om')} ${r.hourly.as_of}: ${formatCurrency2(td.cmp_avg_per_receipt)}`
                  : `${cmpWord}: ${formatCurrency2(r.cmp_kpis.avg_per_receipt)}`} accent="mint" icon={TrendingUp} />
              {type === 'day' ? (
                <KpiTile {...drillProps} label={t('Korting')} value={formatCurrency(r.kpis.discount ?? 0)}
                  sub={t('tik voor de bonnen met korting')} accent="slate" icon={CalendarDays} />
              ) : (
                <KpiTile {...drillProps} label={t('Omzet per open dag')} value={formatCurrency(r.kpis.revenue_per_day)} delta={r.deltas.revenue_per_day} deltaLabel={cmpLabel}
                  sub={`${r.kpis.open_days} ${t('open dagen')} (${cmpWord} ${r.cmp_kpis.open_days})`} accent="gold" icon={CalendarDays} />
              )}
            </>;
          })()}
        </div>
      )}

      {r && type === 'day' && !r.kpis.receipts && (
        <p className="text-sm text-slate-500 mb-4 rounded-lg border border-slate-200 bg-white px-3 py-2">
          {isToday(win.start)
            ? t('Nog geen bonnen vandaag. De cijfers verversen automatisch zodra er verkocht wordt.')
            : t('Geen bonnen op deze dag — het café was gesloten.')}
        </p>
      )}

      {r && type === 'day' && r.hourly?.hours?.length > 0 && (
        <SectionCard className="mb-4" title={t('Verloop van de dag')}
          subtitle={hourMode === 'cum'
            ? `${t('omzet opgeteld per uur')} · ${t('grijze stippellijn')} = ${avgMode ? t('een normale dag') : t('de vergelijkingsperiode')}`
            : `${t('omzet per uur')} · ${t('grijze stippellijn')} = ${avgMode ? t('een normale dag') : t('de vergelijkingsperiode')}`}
          right={<PillToggle value={hourMode} onChange={setHourMode} options={[
            { value: 'cum', label: t('Opgeteld') }, { value: 'hour', label: t('Per uur') },
          ]} />}>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={r.hourly.hours} margin={{ left: 4, right: 12, top: 26 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={8} />
              {/* a correction rung up before opening can start the day below zero;
                  pin the axis to it instead of letting the ticks reserve the space */}
              <YAxis tick={{ fontSize: 11 }} {...(hourAxis || {})}
                tickFormatter={(v) => `€${v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : Math.round(v)}`} />
              <Tooltip content={<ChartTooltip money={['revenue', 'cmp_revenue', 'cum_revenue', 'cmp_cum_revenue']}
                deltas={{ revenue: 'cmp_revenue', cum_revenue: 'cmp_cum_revenue' }} />} />
              {r.hourly.as_of && (
                <ReferenceLine x={`${r.hourly.as_of.slice(0, 2)}:00`} stroke={CALUM.green} strokeDasharray="2 3"
                  label={{ value: t('nu'), position: 'insideTop', offset: -16, fontSize: 10, fontWeight: 600, fill: CALUM.green }} />
              )}
              <Line name={avgMode ? t('Normale dag') : t('Vergelijking')} dataKey={hourMode === 'cum' ? 'cmp_cum_revenue' : 'cmp_revenue'}
                stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5 4" dot={false} connectNulls />
              {hourMode === 'cum' ? (
                <Area name={t('Vandaag')} type="monotone" dataKey="cum_revenue" stroke={CALUM.gold} strokeWidth={2}
                  fill={CALUM.gold} fillOpacity={0.16} connectNulls={false} dot={{ r: 2, fill: CALUM.gold }}>
                  <LabelList dataKey="cum_delta_pct" content={<DeltaLabel />} />
                </Area>
              ) : (
                <Bar name={t('Vandaag')} dataKey="revenue" fill={CALUM.gold} radius={[3, 3, 0, 0]}>
                  <LabelList dataKey="delta_pct" content={<DeltaLabel />} />
                </Bar>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </SectionCard>
      )}

      <SectionCard className="mb-4" title={t("Prognose")}
        subtitle={`${t("verwachte rest = gemiddelde per weekdag van de laatste 8 weken")}${fc.data?.as_of ? ` · ${t("Data t/m")} ${formatDate(fc.data.as_of)}` : ''} · ${t("Altijd de huidige maand en dit jaar, los van de gekozen periode")}`}>
        <QueryState q={fc} isEmpty={(d) => !d.month}>
          {() => (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-1">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">
                  {formatMonth(fc.data.month.month)} · {fc.data.month.complete ? t('afgesloten') : t('prognose')}
                </div>
                <div className="text-2xl font-bold text-slate-900 tabular-nums" style={{ fontFamily: 'Outfit, Inter, sans-serif' }}>
                  {formatCurrency(fc.data.month.projected)}
                </div>
                <div className="text-xs tabular-nums mt-0.5">
                  <Pct v={fc.data.month.yoy_pct} />{' '}
                  {fc.data.month.prev_year != null && <span className="text-slate-400">vs {formatCurrency(fc.data.month.prev_year)} {t("vorig jaar")}</span>}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {formatCurrency(fc.data.month.so_far)} {t("gerealiseerd")}
                  {fc.data.month.days_remaining > 0 && <> · {t("nog")} {fc.data.month.days_remaining} {t("open dagen")}</>}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1">
                  {fc.data.year.year} · {fc.data.year.projected != null ? t('prognose heel jaar') : t('Tot nu toe dit jaar')}
                </div>
                <div className="text-2xl font-bold text-slate-900 tabular-nums" style={{ fontFamily: 'Outfit, Inter, sans-serif' }}>
                  {formatCurrency(fc.data.year.projected ?? fc.data.year.ytd)}
                </div>
                <div className="text-xs tabular-nums mt-0.5">
                  <Pct v={fc.data.year.yoy_pct} />{' '}
                  {fc.data.year.prev_year_total != null && <span className="text-slate-400">vs {formatCurrency(fc.data.year.prev_year_total)} {t("in")} {fc.data.year.year - 1}</span>}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {t('Tot nu toe dit jaar')}: {formatCurrency(fc.data.year.ytd)}
                  {fc.data.year.ytd_growth_pct != null && <> · {t("groei")} {String(fc.data.year.ytd_growth_pct).replace('.', ',')}% — {t("rest van het jaar = vorig jaar × deze groei")}</>}
                </div>
              </div>
            </div>
          )}
        </QueryState>
      </SectionCard>

      {r && (
        <>
          {win.start !== win.end && (
          <SectionCard title={weeklyMode ? t('Omzet per week') : t('Omzet per dag')}
            subtitle={avgMode ? t('Grijze stippellijn = een normale dag van die weekdag') : t('Grijze stippellijn = vergelijkingsperiode (op volgorde gelegd)')}>
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={chart} margin={{ left: 4, right: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={14} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : v}`} />
                <Tooltip content={<ChartTooltip money={['revenue', 'cmp_revenue']} deltas={{ revenue: 'cmp_revenue' }} />} />
                <Bar name={t('Omzet')} dataKey="revenue" fill={CALUM.gold} radius={[3, 3, 0, 0]} />
                <Line name={t('Vergelijking')} dataKey="cmp_revenue" stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="5 4" dot={false} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </SectionCard>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mt-4">
            <SectionCard className="lg:col-span-3" title={t('Omzetgroepen')}
              subtitle={t('Verkoop per hoofdcategorie (incl. btw, na korting) — zoals in LightSpeed · t.o.v. de vergelijkingsperiode')}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[520px]">
                  <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
                    <th className="py-1">{t('Groep')}</th><th className="w-1/5"></th>
                    <th className="text-right">{t('Aandeel')}</th><th className="text-right">{t('Bedrag')}</th>
                    <th className="text-right">{cmpWord}</th><th className="text-right">Δ</th>
                    <th className="text-right hidden md:table-cell">{t('Bonnen')}</th><th className="text-right hidden md:table-cell">€/{t('bon')}</th>
                  </tr></thead>
                  <tbody>
                    {r.groups.map((g) => (
                      <tr key={g.group} className="border-t border-slate-100">
                        <td className="py-1.5 font-medium text-slate-700 whitespace-nowrap">{t(GRP_LABEL[g.group] || g.group)}</td>
                        <td className="px-2">
                          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${g.pct_amount}%`, background: grpColor(g.group) }} />
                          </div>
                        </td>
                        <td className="text-right tabular-nums text-slate-500">{String(g.pct_amount).replace('.', ',')}%</td>
                        <td className="text-right tabular-nums font-semibold">{formatCurrency(g.amount)}</td>
                        <td className="text-right tabular-nums text-slate-500">{g.cmp_amount != null ? formatCurrency(g.cmp_amount) : '—'}</td>
                        <td className="text-right tabular-nums"><Pct v={g.chg_pct} /></td>
                        <td className="text-right tabular-nums hidden md:table-cell">{formatNumber(g.orders)}</td>
                        <td className="text-right tabular-nums hidden md:table-cell">{formatCurrency2(g.avg_per_order)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                  {t("Bonnen per groep telt elke bon met minstens één artikel uit die groep, dus de som ligt hoger dan het totaal aantal bonnen (een bon bevat vaak meerdere groepen).")}
                </p>
              </div>
            </SectionCard>

            <SectionCard className="lg:col-span-2" title={t('Dagdelen & weekdagen')} subtitle={t('gemiddelde omzet per open dag')}>
              <div className="space-y-2 pt-1">
                {r.dayparts.map((d) => (
                  <div key={d.key} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600">{t(d.label)}</span>
                    <span className="tabular-nums font-semibold">{formatCurrency(d.revenue)} <span className="font-normal text-slate-400">· {String(d.pct_of_revenue).replace('.', ',')}%</span> <Pct v={d.chg_pct} /></span>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto mt-4">
                <table className="w-full text-xs">
                  <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
                    <th className="py-1">{t('Weekdag')}</th><th className="text-right">{t('Nu')}</th><th className="text-right">{cmpWord}</th><th className="text-right">Δ</th>
                  </tr></thead>
                  <tbody>
                    {r.weekdays.filter((w) => w.cur || w.cmp).map((w) => (
                      <tr key={w.weekday_nl} className="border-t border-slate-100">
                        <td className="py-1">{weekdayLabel(w.weekday_nl)}</td>
                        <td className="text-right tabular-nums">{formatCurrency(w.cur)}</td>
                        <td className="text-right tabular-nums text-slate-500">{formatCurrency(w.cmp)}</td>
                        <td className="text-right tabular-nums"><Pct v={w.chg_pct} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            {[{ key: 'risers', title: t('Grootste stijgers'), rows: r.risers },
              { key: 'fallers', title: t('Grootste dalers'), rows: r.fallers }].map(({ key, title, rows }) => (
              <SectionCard key={key} title={title} subtitle={t('omzetverschil in € t.o.v. de vergelijkingsperiode')}>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
                      <th className="py-1">{t('Product')}</th><th className="text-right">{t('Stuks')}</th>
                      <th className="text-right">{t('Omzet')}</th><th className="text-right">{cmpWord}</th><th className="text-right">Δ €</th>
                    </tr></thead>
                    <tbody>
                      {rows.map((m) => (
                        <tr key={m.name} className="border-t border-slate-100">
                          <td className="py-1"><span className="block truncate max-w-[160px]" title={m.name}>{m.name}</span></td>
                          <td className="text-right tabular-nums">{formatNumber(m.units)}</td>
                          <td className="text-right tabular-nums">{formatCurrency(m.revenue)}</td>
                          <td className="text-right tabular-nums text-slate-500">{formatCurrency(m.cmp_revenue)}</td>
                          <td className={`text-right tabular-nums font-semibold ${m.delta >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                            {m.delta >= 0 ? '+' : ''}{formatCurrency(m.delta)}
                          </td>
                        </tr>
                      ))}
                      {!rows.length && <tr><td className="py-2 text-slate-400" colSpan={5}>{t('Geen noemenswaardige verschuivingen.')}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            ))}
          </div>

          {r.daily.length > 1 && r.daily.length <= 32 && (
            <SectionCard className="mt-4" title={t('Dag per dag')}
              subtitle={`${t('vlag = afwijking van ±20% t.o.v. dezelfde weekdag in de vergelijkingsperiode')} · ${t('tik een dag voor de bonnen')}`}>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
                    <th className="py-1">{t('Datum')}</th><th>{t('Weekdag')}</th>
                    <th className="text-right">{t('Bonnen')}</th><th className="text-right">{t('Omzet')}</th><th className="text-right">{t('vs verwacht')}</th>
                  </tr></thead>
                  <tbody>
                    {r.daily.map((d) => (
                      <tr key={d.date} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50"
                        onClick={() => openDrill(d.date, d.date, `${weekdayLabel(d.weekday_nl)} ${formatDate(d.date)}`)}>
                        <td className="py-1 whitespace-nowrap">{formatDate(d.date)}</td>
                        <td className="text-slate-500">{weekdayLabel(d.weekday_nl)}</td>
                        <td className="text-right tabular-nums">{formatNumber(d.receipts)}</td>
                        <td className="text-right tabular-nums">{formatCurrency(d.revenue)}</td>
                        <td className="text-right tabular-nums">
                          {d.vs_expected_pct != null && Math.abs(d.vs_expected_pct) >= 20
                            ? <b><Pct v={d.vs_expected_pct} /></b>
                            : <Pct v={d.vs_expected_pct} />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          )}
        </>
      )}

      <SectionCard className="mt-4" title={`${t("Groei t.o.v. vorig jaar")}${gd ? ` — ${gd.prev_year} vs ${gd.year}` : ''}`}
        subtitle={`${gd?.totals ? `${t("Volledige maanden in beide jaren · café-omzet · totaal")} ${gd.totals.chg_pct >= 0 ? '+' : ''}${String(gd.totals.chg_pct).replace('.', ',')}% · ` : ''}${fixedNote}`}>
        <QueryState q={gr} isEmpty={(d) => !d.months?.length}>
          {() => (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t("Per maand (€)")}</div>
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={yoyMonths} margin={{ left: 0, right: 8, top: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                    <Tooltip content={<ChartTooltip money={['prev', 'cur']} />} />
                    <Bar name={`${gd.prev_year}`} dataKey="prev" fill="#94a3b8" radius={[3, 3, 0, 0]} />
                    <Bar name={`${gd.year}`} dataKey="cur" fill={CALUM.gold} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-[11px] text-slate-400 mt-1">{t("Vergelijk altijd met dezelfde maand vorig jaar (YoY) — niet met de vorige maand; een café leeft op seizoenen.")}</p>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t("Waar komt de groei vandaan? Volume vs prijs")}</div>
                {gd.decomposition && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[300px]">
                      <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
                        <th className="py-1"></th><th className="text-right">{gd.prev_year}</th><th className="text-right">{gd.year}</th><th className="text-right">Δ</th>
                      </tr></thead>
                      <tbody>
                        <tr className="border-t border-slate-100"><td className="py-1.5">{t("Bonnen")}</td><td className="text-right tabular-nums">{formatNumber(gd.decomposition.receipts.prev)}</td><td className="text-right tabular-nums">{formatNumber(gd.decomposition.receipts.cur)}</td><td className="text-right tabular-nums"><Pct v={gd.decomposition.receipts.chg_pct} /></td></tr>
                        <tr className="border-t border-slate-100"><td className="py-1.5">{t("Artikelen per bon")}</td><td className="text-right tabular-nums">{String(gd.decomposition.items_per_ticket.prev).replace('.', ',')}</td><td className="text-right tabular-nums">{String(gd.decomposition.items_per_ticket.cur).replace('.', ',')}</td><td className="text-right tabular-nums"><Pct v={gd.decomposition.items_per_ticket.chg_pct} /></td></tr>
                        <tr className="border-t border-slate-100"><td className="py-1.5">{t("Prijs per artikel")}</td><td className="text-right tabular-nums">{formatCurrency2(gd.decomposition.price_per_item.prev)}</td><td className="text-right tabular-nums">{formatCurrency2(gd.decomposition.price_per_item.cur)}</td><td className="text-right tabular-nums"><Pct v={gd.decomposition.price_per_item.chg_pct} /></td></tr>
                        <tr className="border-t border-slate-100 font-semibold"><td className="py-1.5">{t("Omzet")}</td><td className="text-right tabular-nums">{formatCurrency(gd.decomposition.revenue.prev)}</td><td className="text-right tabular-nums">{formatCurrency(gd.decomposition.revenue.cur)}</td><td className="text-right tabular-nums"><Pct v={gd.decomposition.revenue.chg_pct} /></td></tr>
                      </tbody>
                    </table>
                  </div>
                )}
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">{t("Omzetgroei = meer bonnen × artikelen per bon × prijs per artikel. Zo zie je meteen wélke van de drie beweegt.")}</p>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-2">{t("Prijspeil van de vaste toppers")}</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[340px]">
                    <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 text-left">
                      <th className="py-1">{t("Item")}</th><th className="text-right">{gd.prev_year}</th><th className="text-right">{gd.year}</th><th className="text-right">Δ {t("prijs")}</th><th className="text-right">Δ {t("volume")}</th>
                    </tr></thead>
                    <tbody>
                      {(gd.price_levels || []).slice(0, 8).map((it) => (
                        <tr key={it.name} className="border-t border-slate-100">
                          <td className="py-1"><span className="block truncate max-w-[140px]" title={it.name}>{it.name}</span></td>
                          <td className="text-right tabular-nums">{formatCurrency2(it.price_prev)}</td>
                          <td className="text-right tabular-nums">{formatCurrency2(it.price_cur)}</td>
                          <td className="text-right tabular-nums"><Pct v={it.price_chg_pct} /></td>
                          <td className="text-right tabular-nums"><Pct v={it.units_chg_pct} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </QueryState>
      </SectionCard>

      {drill && (
        <ReceiptsModal start={drill.start} end={drill.end} label={drill.label} onClose={() => setDrill(null)} />
      )}
    </PageShell>
  );
}
